import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IconButton } from '@xing-com/crate-lebenslauf-button';
import type { Template } from '@xing-com/crate-lebenslauf-constants';
import { useBasePath } from '@xing-com/crate-lebenslauf-hooks';
import { IconArrowLeft, IconArrowRight } from '@xing-com/icons';
import { Image } from '@xing-com/image';

import { Styled } from './turntable.styled';

const SHOW = 3;

export const Turntable: React.FC<{
  slides: Template[];
  setCurrentBackgroundColor: React.Dispatch<React.SetStateAction<string>>;
  hideCTA?: boolean;
}> = ({ slides, setCurrentBackgroundColor, hideCTA }) => {
  const intl = useIntl();
  const basePath = useBasePath();
  const [slidesToRender, setSlidesToRender] = useState([...slides, ...slides]);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [liveMessage, setLiveMessage] = useState('');

  const templateName = slidesToRender[currentIndex + 1].name;
  const templateTitle = slidesToRender[currentIndex + 1].title;
  const activeTemplateLink = `${basePath}/neu#${slidesToRender[
    currentIndex + 1
  ].createWith.template.toLowerCase()}-${slidesToRender[
    currentIndex + 1
  ].createWith.color.toLowerCase()}`;

  const handlePrevClick = (): void =>
    setCurrentIndex((prevState) => prevState - 1);

  const handleNextClick = (): void =>
    setCurrentIndex((prevState) => prevState + 1);

  useEffect(() => {
    setLiveMessage(
      intl.formatMessage(
        {
          id: 'CRATE_LEBENSLAUF_SELECTED_TEMPLATE',
        },
        {
          templateName: intl.$t({
            id: templateTitle,
          }),
        }
      )
    );
  }, [currentIndex, intl, templateName]);

  useEffect(() => {
    if (currentIndex === slidesToRender.length - 2) {
      slides.forEach((slide) =>
        setSlidesToRender((prevState) => [...prevState, slide])
      );
    }
  }, [currentIndex, slides, slidesToRender.length]);

  useEffect(() => {
    if (currentIndex === 0) {
      slides
        .slice()
        .reverse()
        .forEach((slide) =>
          setSlidesToRender((prevState) => [slide, ...prevState])
        );
      setCurrentIndex(slides.length);
    }
  }, [currentIndex, slides]);

  const bgColor = slidesToRender?.[currentIndex + 1]?.bgColor;

  useEffect(() => {
    if (bgColor) setCurrentBackgroundColor(bgColor);
  }, [setCurrentBackgroundColor, bgColor]);

  return (
    <Styled.Container>
      <Styled.VisuallyHidden
        tabIndex={-1}
        aria-live="polite"
        aria-atomic="true"
      >
        {liveMessage}
      </Styled.VisuallyHidden>
      <Styled.SliderContainer aria-roledescription="carousel">
        <Styled.Slides
          role="listbox"
          aria-label="select template"
          $translation={currentIndex * (100 / SHOW)}
          tabIndex={-1}
        >
          {slidesToRender.map((slide, index) => {
            if (currentIndex === index - 1) {
              return (
                <Styled.ActiveSlide
                  role="option"
                  aria-selected="true"
                  to={activeTemplateLink}
                  key={index}
                  tabIndex={0}
                  aria-label={intl.$t({ id: slide.title })}
                >
                  <Image
                    src={slide.imageUrl}
                    // @ts-expect-error FIXME: SC6
                    srcSet={`${slide.imageUrl} 1x, ${slide.imageUrl2x} 2x`}
                    loading="lazy"
                    width="595"
                    height="842"
                    alt=""
                    aria-hidden="true"
                  />
                </Styled.ActiveSlide>
              );
            }
            return (
              <Styled.Slide
                onClick={
                  currentIndex === index ? handlePrevClick : handleNextClick
                }
                key={index}
                role="option"
                aria-selected="false"
                tabIndex={-1}
                aria-hidden="true"
              >
                <Image
                  src={slide.imageUrl}
                  // @ts-expect-error FIXME: SC6
                  srcSet={`${slide.imageUrl} 1x, ${slide.imageUrl2x} 2x`}
                  loading="lazy"
                  width="595"
                  height="842"
                  alt={slide.title}
                />
              </Styled.Slide>
            );
          })}
        </Styled.Slides>
        <Styled.Controls aria-label="controls">
          <IconButton
            icon={IconArrowLeft}
            onClick={handlePrevClick}
            role="button"
            size="medium"
            // @ts-expect-error FIXME: SC6
            tabIndex="0"
            variant="tertiary"
            aria-label={intl.formatMessage({
              id: 'CRATE_VITA_STARTPAGE_PREVIOUS_TEMPLATE_BUTTON',
            })}
          />
          <IconButton
            icon={IconArrowRight}
            onClick={handleNextClick}
            role="button"
            size="medium"
            // @ts-expect-error FIXME: SC6
            variant="tertiary"
            aria-label={intl.formatMessage({
              id: 'CRATE_VITA_STARTPAGE_NEXT_TEMPLATE_BUTTON',
            })}
          />
        </Styled.Controls>
      </Styled.SliderContainer>
      {!hideCTA && (
        <Styled.CTAButton
          variant="primary"
          size="medium"
          to={activeTemplateLink}
        >
          <FormattedMessage id="CRATE_VITA_STARTPAGE_TURNTABLE_CTA" />
        </Styled.CTAButton>
      )}
    </Styled.Container>
  );
};
